<div class="mx-auto flex max-w-4xl flex-col gap-4 p-4" data-testid="integrations-cards">

  <div>
    <h1 class="text-xl font-bold tracking-tight text-gray-900">Integrationen</h1>
    <p class="mt-1 text-sm text-gray-500">
      Zugangsdaten und Schlüssel der Anbieter, die für die gesamte Installation gelten. Sie gelten nicht für
      ein einzelnes Unternehmen.
    </p>
  </div>

  <!-- ── Fiskaly ──────────────────────────────────────────────────────────────────────────────────────────
       Two independent facts on one card: the management credentials and the managing organisation id. A
       missing organisation id refuses EVERY shard registration even while the credentials are correct, so it
       gets its own chip rather than being folded into a single "fiskaly is fine" verdict. -->
  <section aria-labelledby="fiskaly-title"
           class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <img alt="" class="h-10 w-10 flex-none rounded-xl bg-gray-50" height="200"
             ngSrc="assets/images/company_logos/logo_fiskaly.png" width="200">
        <div class="min-w-0">
          <h2 class="text-base font-bold tracking-tight text-gray-800" id="fiskaly-title">Fiskaly</h2>
          <p class="mt-0.5 text-xs text-gray-500">Technische Sicherheitseinrichtung, DSFinV-K und Beleg</p>
        </div>
      </div>
      <span [class]="chipClass(fiskalyChip())"
            class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold">
        {{ fiskalyChip().label }}
      </span>
    </div>

    <!-- The organisation id sits in the open: it is the field an operator is most likely to be missing, and
         the one whose absence produces a refusal on a completely different screen. -->
    <div class="mt-4">
      <div class="flex flex-wrap items-center justify-between gap-2">
        <label class="block text-sm font-bold tracking-tight text-gray-700" for="fiskaly-organization">
          Organisations-ID
        </label>
        <span [class]="chipClass(organizationChip())"
              class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold"
              data-testid="organization-chip">
          {{ organizationChip().label }}
        </span>
      </div>
      <div class="mt-2 flex flex-col gap-2 sm:flex-row">
        <input (input)="organizationId.set(value($event))" [value]="organizationId()"
               class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                      shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                      focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
               id="fiskaly-organization" name="fiskaly-organization" placeholder="z. B. 8b2c…"
               type="text">
        <button (click)="saveOrganization(organizationId())" [disabled]="savingOrganization()"
                class="inline-flex shrink-0 items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                       py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                       duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                       disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale sm:text-sm"
                type="button">
          Speichern
        </button>
      </div>
      <p class="mt-2 text-xs text-gray-500">
        Sie finden die ID im Fiskaly-Dashboard unter „Organizations". Ohne sie lässt sich kein Standort bei
        Fiskaly registrieren.
      </p>
    </div>

    <!-- Credentials behind a disclosure: they are already set on every running installation, so re-entering
         them is the rarer act. It opens itself when the status says they are not correct. -->
    <div class="mt-4 border-t border-gray-100 pt-3">
      <button (click)="toggleCredentials()" [attr.aria-expanded]="credentialsOpen()"
              aria-controls="fiskaly-credentials"
              class="group -mx-2 flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-2
                     py-1.5 text-left transition-colors duration-200 hover:bg-gray-50 focus-visible:outline
                     focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-theme-primary-500"
              type="button">
        <span class="text-sm font-bold tracking-tight text-gray-700">Zugangsdaten der Verwaltung</span>
        <span class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border-[1px] border-gray-200 bg-white
                     px-2.5 py-1 text-sm font-medium text-gray-600 shadow-sm transition-colors duration-200
                     group-hover:border-gray-300 group-hover:bg-gray-100 group-hover:text-gray-900">
          <span>{{ credentialsOpen() ? 'Ausblenden' : 'Anzeigen' }}</span>
          <!-- The chevron from the style guide's disclosure pattern, viewBox and path together: its ink spans
               x 6..18 and y 9..15 in a 24x24 box, so the glyph is centred on the box centre and the 180deg
               rotation is symmetrical about it. A filled caret whose ink sits in the TOP half of its box
               (the shape this used to carry, measured at 3.5/16 above centre) reads as mis-aligned closed and
               jumps 7 units when it opens. -->
          <svg [class.rotate-180]="credentialsOpen()" aria-hidden="true"
               class="h-4 w-4 shrink-0 origin-center transition-transform duration-200" fill="none"
               stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
        </span>
      </button>

      <!-- Rendered always, the @if inside: `aria-controls` above must resolve while collapsed. -->
      <div id="fiskaly-credentials">
        @if (credentialsOpen()) {
          <div class="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="fiskaly-email">
                E-Mail
              </label>
              <input #fiskalyEmail
                     class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5
                            text-gray-900 shadow-sm transition-all duration-200 placeholder-gray-300
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                     autocomplete="off" id="fiskaly-email" name="fiskaly-email"
                     placeholder="name@example.de" type="email">
            </div>
            <div>
              <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="fiskaly-password">
                Passwort
              </label>
              <input #fiskalyPassword
                     class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5
                            text-gray-900 shadow-sm transition-all duration-200 placeholder-gray-300
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                     autocomplete="new-password" id="fiskaly-password" name="fiskaly-password"
                     type="password">
            </div>
          </div>
          <!-- Helper and button share ONE row from `sm` up, text left and button right: a right-aligned button
               with its explanation on a line of its own leaves the sentence stranded under a stretch of empty
               space, reading as two unrelated things. Below `sm` they stack, sentence first, button full
               width. (The single-field rows above put the button beside the INPUT instead, which is why their
               helper stays under the field.) -->
          <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p class="text-xs text-gray-500">
              Die Zugangsdaten Ihres Fiskaly-Kontos. Sie werden beim Speichern sofort geprüft.
            </p>
            <button (click)="saveCredentials(fiskalyEmail.value, fiskalyPassword.value)"
                    [disabled]="savingCredentials()"
                    class="inline-flex w-full shrink-0 items-center justify-center rounded-2xl
                           bg-theme-primary-600 px-5 py-2.5 font-semibold text-white shadow-lg
                           shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700
                           hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale sm:w-auto sm:text-sm"
                    type="button">
              Speichern
            </button>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- ── Demo-Organisationen (#933) ── its own component: the pool has its own list, form and dialog. -->
  <app-demo-organizations/>

  <!-- ── SumUp ────────────────────────────────────────────────────────────────────────────────────────── -->
  <section aria-labelledby="sumup-title"
           class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <span aria-hidden="true"
              class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-50 text-sm
                     font-bold text-theme-primary-700">
          SU
        </span>
        <div class="min-w-0">
          <h2 class="text-base font-bold tracking-tight text-gray-800" id="sumup-title">SumUp</h2>
          <p class="mt-0.5 text-xs text-gray-500">Kartenzahlung an der Kasse</p>
        </div>
      </div>
      <span [class]="chipClass(sumupChip())"
            class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold">
        {{ sumupChip().label }}
      </span>
    </div>

    <div class="mt-4">
      <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="sumup-affiliate-key">
        Affiliate Key
      </label>
      <div class="flex flex-col gap-2 sm:flex-row">
        <input (input)="sumupKey.set(value($event))" [value]="sumupKey()"
               class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                      shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                      focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
               id="sumup-affiliate-key" name="sumup-affiliate-key" placeholder="sup_pk_…" type="text">
        <button (click)="saveSumup(sumupKey())" [disabled]="savingSumup()"
                class="inline-flex shrink-0 items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                       py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                       duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                       disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale sm:text-sm"
                type="button">
          Speichern
        </button>
      </div>
      <p class="mt-2 text-xs text-gray-500">
        Gilt für die gesamte App und wird beim nächsten Login auf jedes Gerät geladen.
      </p>
    </div>

    <div class="mt-4 border-t border-gray-100 pt-3">
      <button (click)="toggleSumupTutorial()" [attr.aria-expanded]="sumupTutorialOpen()"
              aria-controls="sumup-tutorial"
              class="group -mx-2 flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl px-2
                     py-1.5 text-left transition-colors duration-200 hover:bg-gray-50 focus-visible:outline
                     focus-visible:outline-2 focus-visible:outline-offset-2
                     focus-visible:outline-theme-primary-500"
              type="button">
        <span class="text-sm font-bold tracking-tight text-gray-700">So erhalten Sie den Affiliate Key</span>
        <span class="inline-flex shrink-0 items-center gap-1.5 rounded-lg border-[1px] border-gray-200 bg-white
                     px-2.5 py-1 text-sm font-medium text-gray-600 shadow-sm transition-colors duration-200
                     group-hover:border-gray-300 group-hover:bg-gray-100 group-hover:text-gray-900">
          <span>{{ sumupTutorialOpen() ? 'Ausblenden' : 'Anzeigen' }}</span>
          <!-- The chevron from the style guide's disclosure pattern, viewBox and path together: its ink spans
               x 6..18 and y 9..15 in a 24x24 box, so the glyph is centred on the box centre and the 180deg
               rotation is symmetrical about it. A filled caret whose ink sits in the TOP half of its box
               (the shape this used to carry, measured at 3.5/16 above centre) reads as mis-aligned closed and
               jumps 7 units when it opens. -->
          <svg [class.rotate-180]="sumupTutorialOpen()" aria-hidden="true"
               class="h-4 w-4 shrink-0 origin-center transition-transform duration-200" fill="none"
               stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
        </span>
      </button>

      <!-- The two links below are underlined at REST, not only on hover: a link inside a text block may not be
           distinguished by colour alone (axe `link-in-text-block`, serious), and the theme pink measures
           1.25:1 against the surrounding gray. The old page carried exactly this violation. -->
      <div id="sumup-tutorial">
        @if (sumupTutorialOpen()) {
          <ol class="mt-2 list-decimal space-y-1 pl-5 text-xs text-gray-600">
            <li>
              Melden Sie sich bei einem
              <a class="text-theme-primary-700 underline hover:no-underline" href="https://developer.sumup.com/"
                 rel="noopener" target="_blank">SumUp Developer-Konto</a> an oder erstellen Sie eines.
            </li>
            <li>
              Wechseln Sie im Developer-Dashboard zu
              <a class="text-theme-primary-700 underline hover:no-underline"
                 href="https://developer.sumup.com/protected/affiliate-keys/" rel="noopener"
                 target="_blank">Affiliate keys</a>.
            </li>
            <li>Erstellen Sie einen neuen Affiliate Key für das iOS-SDK und hinterlegen Sie die App-Bundle-ID,
              falls danach gefragt wird.
            </li>
            <li>Kopieren Sie den erzeugten Key.</li>
            <li>Fügen Sie ihn oben ein und speichern Sie ihn.</li>
          </ol>
        }
      </div>
    </div>
  </section>

  <!-- ── Telegram ─────────────────────────────────────────────────────────────────────────────────────────
       The token is a catalogued secret: it is never returned by the GET, so the field is never prefilled and
       is cleared after a successful save. -->
  <section aria-labelledby="telegram-title"
           class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <span aria-hidden="true"
              class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-50
                     text-theme-primary-700">
          <svg class="h-6 w-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9.78 18.65l.28-4.23l7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3L3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42"
              fill="currentColor"/>
          </svg>
        </span>
        <div class="min-w-0">
          <h2 class="text-base font-bold tracking-tight text-gray-800" id="telegram-title">Telegram</h2>
          <p class="mt-0.5 text-xs text-gray-500">Bot für Kontaktverknüpfung und Passwortvergabe</p>
        </div>
      </div>
      <span [class]="chipClass(telegramChip())"
            class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold"
            data-testid="telegram-chip">
        {{ telegramChip().label }}
      </span>
    </div>

    <div class="mt-4">
      <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="telegram-token">
        Bot-Token
      </label>
      <div class="flex flex-col gap-2 sm:flex-row">
        <input (input)="telegramToken.set(value($event))" [value]="telegramToken()"
               class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                      shadow-sm transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                      focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
               autocomplete="new-password" id="telegram-token" name="telegram-token"
               placeholder="Neues Token eingeben" type="password">
        <button (click)="saveTelegramToken(telegramToken())" [disabled]="savingTelegram()"
                class="inline-flex shrink-0 items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                       py-2.5 font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                       duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                       disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale sm:text-sm"
                type="button">
          Speichern
        </button>
      </div>
      <p class="mt-2 text-xs text-gray-500">
        Das Token erhalten Sie in Telegram von &#64;BotFather. Ein gespeichertes Token wird aus
        Sicherheitsgründen nie wieder angezeigt.
      </p>
      <p class="mt-1 text-xs text-gray-500">
        Der Passwort-Bot startet nach dem nächsten Neustart des Dienstes.
      </p>
    </div>
  </section>

  <!-- ── DNS (#637) ───────────────────────────────────────────────────────────────────────────────────────
       The card reports two things the other three do not have to: whether the instance's OWN address could be
       read (zone and Ziel-IP are mirrored from it, so without it nothing can be registered) and what is still
       outstanding in the ledger. A DNS failure never blocks an alias save, so the queue is the only place a
       silently-stopped automation becomes visible. The token is a catalogued secret: never prefilled, cleared
       after every answer. -->
  <section aria-labelledby="dns-title"
           class="rounded-2xl border-[1px] border-gray-100 bg-white p-4 shadow-sm">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <span aria-hidden="true"
              class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gray-50
                     text-theme-primary-700">
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"
               xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3a9 9 0 100 18 9 9 0 000-18zm0 0c2.5 2.5 3.75 5.5 3.75 9S14.5 18.5 12 21m0-18
                     c-2.5 2.5-3.75 5.5-3.75 9S9.5 18.5 12 21M3.5 9h17m-17 6h17"
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
          </svg>
        </span>
        <div class="min-w-0">
          <h2 class="text-base font-bold tracking-tight text-gray-800" id="dns-title">DNS</h2>
          <p class="mt-0.5 text-xs text-gray-500">Web-Adressen der Unternehmens-Aliase</p>
        </div>
      </div>
      <span [class]="chipClass(dnsChip())"
            class="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 align-top text-xs font-semibold"
            data-testid="dns-chip">
        {{ dnsChip().label }}
      </span>
    </div>

    <!-- Picker and token stack on a phone and share a row from `sm` up; the token column only exists for
         Cloudflare, which is the only provider carrying a credential. -->
    <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="dns-provider">
          DNS-Anbieter
        </label>
        <!-- `ariaLabel` is what names the inner search input: a <label for> on the cqme-select host does not
             reach it, because the host is not a labelable element. -->
        <cqme-select (currentChange)="onDnsProviderPicked($event)" [current]="dnsProvider()"
                     [disabled]="savingDns()" [labelKey]="'label'" [options]="dnsProviderOptions"
                     [valueKey]="'value'" ariaLabel="DNS-Anbieter dieser Installation"
                     data-testid="dns-provider" id="dns-provider" size="md"/>
      </div>
      @if (dnsNeedsToken()) {
        <div>
          <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="dns-token">
            API-Token
          </label>
          <input (input)="dnsToken.set(value($event))" [value]="dnsToken()"
                 class="block w-full rounded-xl border-[1px] border-gray-200 bg-white px-3 py-2.5 text-gray-900
                        shadow-sm transition-all duration-200 placeholder-gray-300
                        focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"
                 autocomplete="new-password" id="dns-token" name="dns-token"
                 placeholder="Neues Token eingeben" type="password">
        </div>
      }
    </div>

    <!-- Helper and the two actions share ONE row from `sm` up, text left and buttons right — the pattern the
         fiskaly credentials panel uses, and for the same reason: a right-aligned button with its explanation
         on a line of its own leaves the sentence stranded under empty space. -->
    <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <p class="text-xs text-gray-500">
        @if (dnsNeedsToken()) {
          Das Token erstellen Sie in Cloudflare unter „My Profile" → „API Tokens" mit den Rechten „Zone:Read"
          und „DNS:Edit". Ein gespeichertes Token wird aus Sicherheitsgründen nie wieder angezeigt.
        } @else {
          Ohne Anbieter werden die Aliase nur vorgemerkt. CheqMe legt dann keine Web-Adressen an.
        }
      </p>
      <div class="flex flex-col gap-2 sm:flex-row sm:shrink-0">
        <button (click)="reconcileDns()" [disabled]="reconcilingDns()"
                class="inline-flex w-full items-center justify-center rounded-2xl border-[1px] border-gray-200
                       bg-white px-5 py-2.5 font-semibold text-gray-700 shadow-sm transition-all duration-200
                       hover:border-gray-300 hover:bg-gray-50 active:scale-95 disabled:cursor-not-allowed
                       disabled:opacity-30 disabled:grayscale sm:w-auto sm:text-sm"
                type="button">
          {{ reconcilingDns() ? 'Wird abgeglichen …' : 'Jetzt abgleichen' }}
        </button>
        <button (click)="saveDns()" [disabled]="savingDns()"
                class="inline-flex w-full shrink-0 items-center justify-center rounded-2xl
                       bg-theme-primary-600 px-5 py-2.5 font-semibold text-white shadow-lg
                       shadow-theme-primary-100 transition-all duration-200 hover:bg-theme-primary-700
                       hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                       disabled:grayscale sm:w-auto sm:text-sm"
                type="button">
          Speichern
        </button>
      </div>
    </div>

    <!-- What the installation itself resolves to. Without this record there is nothing to mirror, so the
         sentence names the field on the sibling screen that fixes it rather than reporting a failure. -->
    @if (dnsStatus(); as status) {
      <div class="mt-4 border-t border-gray-100 pt-3">
        @if (status.mainHost) {
          <p class="text-xs text-gray-600" data-testid="dns-main-host">
            Hauptadresse: <span class="font-semibold text-gray-800">{{ status.mainHost }}</span>
            @if (status.mainRecordContent) {
              <span class="text-gray-500"> → {{ status.mainRecordContent }}</span>
            }
            @if (status.zoneName) {
              <span class="text-gray-500"> · Zone {{ status.zoneName }}</span>
            }
          </p>
          @if (status.provider !== 'none' && !status.mainRecordFound) {
            <p class="mt-1 text-xs text-red-700" data-testid="dns-main-host-missing">
              Für diese Adresse wurde beim Anbieter kein A-Eintrag gefunden. Solange er fehlt, kann CheqMe
              keine Alias-Adressen anlegen.
            </p>
          }
        } @else {
          <!-- Red only once a provider is chosen: under „Kein Anbieter" nothing is blocked yet, so the same
               sentence is advice (where the address will be read from), not an error the operator must fix
               before anything works. Painting it red on a fresh installation cried wolf on the first visit. -->
          <p class="text-xs" data-testid="dns-main-host-missing"
             [class.text-red-700]="status.provider !== 'none'"
             [class.text-gray-500]="status.provider === 'none'">
            Es ist keine Hauptadresse hinterlegt. Tragen Sie sie unter „Plattform" → „Öffentliche Adresse"
            ein — Zone und Ziel-Adresse der Aliase werden von dort übernommen.
          </p>
        }

        <!-- The probe reports its own failure on a 200 rather than refusing the read, so the card can still
             say what it knows. Rendered from the CODE: a rejected token and a missing zone are two different
             problems with two different remedies. -->
        @if (dnsBlockingReason(status); as reason) {
          <p class="mt-1 text-xs text-red-700" data-testid="dns-blocked">{{ reason }}</p>
        }

        <p class="mt-1 text-xs text-gray-500" data-testid="dns-counts">
          {{ status.registered }} aktiv · {{ status.pending }} ausstehend · {{ status.failed }} fehlgeschlagen
        </p>

        <!-- Only the rows that need somebody. A settled row is the normal case and there can be hundreds. -->
        @if (dnsOpenEntries().length > 0) {
          <ul class="mt-2 space-y-1" data-testid="dns-entries">
            @for (entry of dnsOpenEntries(); track entry.alias) {
              <li class="flex flex-col gap-1 rounded-xl bg-gray-50 px-3 py-2 sm:flex-row sm:items-baseline
                         sm:gap-2">
                <span class="text-xs font-semibold text-gray-800">{{ entry.alias }}</span>
                <span [class]="toneClass(dnsStateTone(entry.state))"
                      class="inline-flex shrink-0 items-center self-start rounded-full px-2 py-0.5 align-top
                             text-xs font-semibold">
                  {{ dnsStateLabel(entry.state) }}
                </span>
                @if (dnsEntryReason(entry); as reason) {
                  <span class="min-w-0 break-words text-xs text-gray-500">{{ reason }}</span>
                }
              </li>
            }
          </ul>
        }
      </div>
    }
  </section>
</div>
